import { useEffect, useRef } from 'react';

const BAR_COUNT = 80;
const BAR_WIDTH = 3;
const BAR_GAP = 2;

export default function InteractiveWaveform() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const timeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0;
    let h = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    const baseFrequencies = Array.from({ length: BAR_COUNT }, () => 0.6 + Math.random() * 0.9);

    const draw = (timestamp: number) => {
      const elapsed = timestamp * 0.001;
      timeRef.current = elapsed;

      ctx.clearRect(0, 0, w, h);

      const step = w / BAR_COUNT;
      const centerY = h / 2;

      for (let i = 0; i < BAR_COUNT; i++) {
        const x = i * step;
        const baseFreq = baseFrequencies[i];

        const amplitude =
          Math.sin(elapsed * baseFreq * 1.4 + i * 0.35) * 0.4 +
          Math.sin(elapsed * baseFreq * 0.7 + i * 0.18) * 0.3 +
          Math.sin(elapsed * baseFreq * 2.1 + i * 0.52) * 0.2 +
          0.1;

        const barHeight = Math.abs(amplitude) * centerY * 0.9;
        const clampedHeight = Math.max(barHeight, 3);
        const alpha = 0.2 + Math.abs(amplitude) * 0.4;
        const barWidth = BAR_WIDTH + Math.abs(amplitude) * 1.5;

        const barX = x + (BAR_GAP * (BAR_WIDTH / (BAR_WIDTH + BAR_GAP))) / 2;

        const gradient = ctx.createLinearGradient(barX, centerY - clampedHeight, barX, centerY + clampedHeight);
        gradient.addColorStop(0, `rgba(120, 200, 255, ${alpha * 0.5})`);
        gradient.addColorStop(0.4, `rgba(100, 180, 245, ${alpha})`);
        gradient.addColorStop(0.5, `rgba(80, 160, 240, ${alpha * 1.1})`);
        gradient.addColorStop(0.6, `rgba(100, 180, 245, ${alpha})`);
        gradient.addColorStop(1, `rgba(120, 200, 255, ${alpha * 0.5})`);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(barX, centerY - clampedHeight, barWidth, clampedHeight * 2, barWidth / 2);
        ctx.fill();
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };

    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      aria-hidden="true"
    />
  );
}